/**
 * Workout bank — structured interval sessions drawn from:
 *   Daniels' Running Formula (E / M / T / I / R framework)
 *   The Science of Running — Magness (fartlek, hill work, CNS activation)
 *   Run Faster — Hudson (general aerobic, progression runs)
 *   Scientific Training for Endurance Athletes — Skiba (cycling zones)
 *   XC-ski sessions: Sandbakk & Holmberg (elite XC physiology & training),
 *     Holmberg / Stöggl & Holmberg (double-poling mechanics & specific strength),
 *     Losnegård (energy-system & uphill demands).
 *
 * Each session has a generate() function that scales rep count to the
 * available time window (total session hours minus warm-up/cool-down).
 * The description format is compatible with parseStructuredWorkout() so
 * TSS is estimated correctly.
 */

import { SportType, TrainingPhase, RaceDistance } from "../types/training";

export type { RaceDistance };

export interface BankSession {
  id: string;
  name: string;
  /** Reference / rationale shown in session detail */
  rationale: string;
  sports: SportType[];
  phases: TrainingPhase[];
  distances: RaceDistance[];
  minCTL: number;
  /** Minimum session duration in hours for this workout to make sense */
  minHours?: number;
  /** Only offered for terrain/trail races (preferHills) — kept out of road rotation. */
  terrainOnly?: boolean;
  /** Training focus. Built-ins resolve it via SESSION_FOCUS; custom sessions set it here. */
  focus?: Focus;
  /** True for user-added sessions (vs. the built-in bank). */
  custom?: boolean;
  generate: (totalHours: number) => string;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

const fmt = (min: number) => Number.isInteger(min) ? `${min}min` : `${Math.round(min)}min`;

// A warmup beyond this is just disguised easy volume in front of a (deliberately
// capped) hard block. Cap it and let the session come out shorter — makeSession
// re-reads the real duration from the text, so the freed time stays on easy days
// and the week stays polarized instead of padding hard days with huge Z2 warmups.
const WARMUP_MAX_MIN = 25;

/** Wrap a quality block with a warmup (capped) that fills remaining time, plus a fixed cooldown. */
function wrapWU(body: string, totalMin: number, qualityMin: number, cdMin = 10): string {
  const wuMin = Math.min(WARMUP_MAX_MIN, Math.max(10, totalMin - qualityMin - cdMin));
  return [`${fmt(wuMin)} Z2 HR`, body, `${fmt(cdMin)} Z1 HR`].join("\n");
}

function scaleReps(availMin: number, workMin: number, restMin: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, Math.floor(availMin / (workMin + restMin))));
}

/** Minutes consumed by N reps of (workSek seconds on + restSek seconds off). */
function repSetMin(reps: number, workSek: number, restSek: number): number {
  return reps * (workSek + restSek) / 60;
}

// ── Running sessions ──────────────────────────────────────────────────────────

const RUN_ALL: SportType[] = ["Run", "NordicSki", "RollerSki"];
const RUN_ONLY: SportType[] = ["Run"];
const SKI: SportType[] = ["NordicSki", "RollerSki"];
const RIDE: SportType[] = ["Ride"];
const ALL_SPORTS: SportType[] = ["Run", "Ride", "NordicSki", "RollerSki"];
export const ALL_PHASES: TrainingPhase[] = ["build", "cutback", "peak", "taper"];
const BUILD_PEAK: TrainingPhase[] = ["build", "peak"];
const ALL_DIST: RaceDistance[] = ["5k", "10k", "half", "marathon", "ultra", "unknown"];
const SHORT: RaceDistance[] = ["5k", "10k", "unknown"];
const LONG: RaceDistance[] = ["half", "marathon", "ultra", "unknown"];

// ─────────────────────────────────────────────────────────────────────────────
// THRESHOLD (T-pace / Z4) — Daniels
// ─────────────────────────────────────────────────────────────────────────────

const cruiseIntervals: BankSession = {
  id: "cruise-intervals",
  name: "Kryssintervaller",
  rationale: "Daniels T-tempo: 3–5 × 8–10 min @ Z4 med 1 min aktiv vila. " +
    "Ökar laktattröskel utan att ackumulera för hög trötthet.",
  sports: RUN_ALL,
  phases: BUILD_PEAK,
  distances: ALL_DIST,
  minCTL: 30,
  generate(totalHours) {
    const totalMin = Math.round(totalHours * 60);
    const reps = scaleReps(totalMin - 20, 10, 2, 2, 5);
    const qualityMin = reps * 12; // 10 work + 2 rest
    const body = [`${reps}x`, `- 10min Z4 HR`, `- 2min Z2 HR`].join("\n");
    return wrapWU(body, totalMin, qualityMin);
  },
};

const continuousTempo: BankSession = {
  id: "continuous-tempo",
  name: "Tempo",
  rationale: "Daniels T-tempo: 20–40 min sammanhängande @ Z4. " +
    "Effektivt för att höja laktattröskel och tävlingstakt.",
  sports: RUN_ALL,
  phases: BUILD_PEAK,
  distances: ALL_DIST,
  minCTL: 35,
  generate(totalHours) {
    const totalMin = Math.round(totalHours * 60);
    const tempoMin = Math.min(40, Math.max(20, totalMin - 20));
    return wrapWU(`${fmt(tempoMin)} Z4 HR`, totalMin, tempoMin);
  },
};

const brokenTempo: BankSession = {
  id: "broken-tempo",
  name: "Brutet tempo",
  rationale: "Magness: 2 × 15–20 min @ Z4 med 2 min vila. " +
    "Ger högre total tröskeltid än ett sammanhängande tempolopp.",
  sports: RUN_ALL,
  phases: BUILD_PEAK,
  distances: LONG,
  minCTL: 40,
  generate(totalHours) {
    const totalMin = Math.round(totalHours * 60);
    const blockMin = Math.min(20, Math.max(12, Math.round((totalMin - 24) / 2)));
    const qualityMin = 2 * (blockMin + 2);
    const body = [`2x`, `- ${fmt(blockMin)} Z4 HR`, `- 2min Z2 HR`].join("\n");
    return wrapWU(body, totalMin, qualityMin);
  },
};

const progressionRun: BankSession = {
  id: "progression-run",
  name: "Progression",
  rationale: "Hudson: börja i Z2, bygg gradvis till Z4 sista tredjedelen. " +
    "Övar taktisk fartdistribution och aerob-anaerob övergång.",
  sports: RUN_ALL,
  phases: BUILD_PEAK,
  distances: LONG,
  minCTL: 30,
  generate(totalHours) {
    const total = Math.round(totalHours * 60);
    const e1 = Math.round(total * 0.40);
    const e2 = Math.round(total * 0.35);
    const e3 = total - e1 - e2;
    return [`${fmt(e1)} Z2 HR`, `${fmt(e2)} Z3 HR`, `${fmt(e3)} Z4 HR`].join("\n");
  },
};

const marathonBlocks: BankSession = {
  id: "marathon-blocks",
  name: "Maratontempoblock",
  rationale: "Daniels M-tempo: 2–3 × 15–20 min i maratontakt (Z3–Z4). " +
    "Tränar metabol effektivitet i tävlingsfart för marathon/ultra.",
  sports: RUN_ONLY,
  phases: BUILD_PEAK,
  distances: ["marathon", "ultra"],
  minCTL: 45,
  generate(totalHours) {
    const totalMin = Math.round(totalHours * 60);
    const reps = scaleReps(totalMin - 20, 18, 3, 2, 3);
    const qualityMin = reps * 21;
    const body = [`${reps}x`, `- 18min Z3 HR`, `- 3min Z2 HR`].join("\n");
    return wrapWU(body, totalMin, qualityMin);
  },
};

// ─────────────────────────────────────────────────────────────────────────────
// VO2MAX (I-pace / Z5) — Daniels
// ─────────────────────────────────────────────────────────────────────────────

const classicVO2max: BankSession = {
  id: "classic-vo2max",
  name: "VO2max-intervaller",
  rationale: "Daniels I-tempo: 4–6 × 4 min @ Z5 med 4 min Z1 vila. " +
    "Maximerar tid vid VO2max, den viktigaste faktorn för 5K–10K prestanda.",
  sports: RUN_ALL,
  phases: BUILD_PEAK,
  distances: SHORT,
  minCTL: 35,
  generate(totalHours) {
    const totalMin = Math.round(totalHours * 60);
    const reps = scaleReps(totalMin - 20, 4, 4, 3, 6);
    const qualityMin = reps * 8;
    const body = [`${reps}x`, `- 4min Z5 HR`, `- 4min Z1 HR`].join("\n");
    return wrapWU(body, totalMin, qualityMin);
  },
};

const shortVO2max: BankSession = {
  id: "short-vo2max",
  name: "Korta VO2max-rep",
  rationale: "Daniels I-tempo: 5–8 × 3 min @ Z5 med 3 min Z1 vila. " +
    "Lättare att hålla hög kvalitet per rep — bra tidigt i bygget.",
  sports: RUN_ALL,
  phases: BUILD_PEAK,
  distances: SHORT,
  minCTL: 30,
  generate(totalHours) {
    const totalMin = Math.round(totalHours * 60);
    const reps = scaleReps(totalMin - 20, 3, 3, 4, 8);
    const qualityMin = reps * 6;
    const body = [`${reps}x`, `- 3min Z5 HR`, `- 3min Z1 HR`].join("\n");
    return wrapWU(body, totalMin, qualityMin);
  },
};

const longVO2max: BankSession = {
  id: "long-vo2max",
  name: "Långa VO2max-rep",
  rationale: "Daniels I-tempo: 3–4 × 5 min @ Z5 med 5 min Z1 vila. " +
    "Bygger syreupptagningskapacitet och mental uthållighet på tävlingsnivå.",
  sports: RUN_ALL,
  phases: ["peak"],
  distances: SHORT,
  minCTL: 50,
  generate(totalHours) {
    const totalMin = Math.round(totalHours * 60);
    const reps = scaleReps(totalMin - 20, 5, 5, 3, 4);
    const qualityMin = reps * 10;
    const body = [`${reps}x`, `- 5min Z5 HR`, `- 5min Z1 HR`].join("\n");
    return wrapWU(body, totalMin, qualityMin);
  },
};

const ladderIntervals: BankSession = {
  id: "ladder",
  name: "Stege-intervaller",
  minHours: 0.75,
  rationale: "Magness: 2–3–4–3–2 min @ Z5 med lika lång Z1 vila. " +
    "Varierar belastningen och håller mentalt engagemang högt.",
  sports: RUN_ALL,
  phases: BUILD_PEAK,
  distances: SHORT,
  minCTL: 40,
  generate(totalHours) {
    const totalMin = Math.round(totalHours * 60);
    const qualityMin = 28; // 2+2+3+3+4+4+3+3+2+2
    const body = [`2min Z5 HR`, `2min Z1 HR`,
      `3min Z5 HR`, `3min Z1 HR`,
      `4min Z5 HR`, `4min Z1 HR`,
      `3min Z5 HR`, `3min Z1 HR`,
      `2min Z5 HR`, `2min Z1 HR`].join("\n");
    return wrapWU(body, totalMin, qualityMin);
  },
};

// ─────────────────────────────────────────────────────────────────────────────
// THRESHOLD + VO2MAX HYBRID
// ─────────────────────────────────────────────────────────────────────────────

const hybridSession: BankSession = {
  id: "threshold-vo2max-hybrid",
  name: "Tröskel + VO2max",
  rationale: "Magness/Hudson: kombinerar Z4-block med avslutande Z5-rep. " +
    "Effektivt för halvmaraton — tränar både tröskel och syreupptagning.",
  sports: RUN_ALL,
  phases: BUILD_PEAK,
  distances: ["10k", "half", "unknown"],
  minCTL: 45,
  generate(totalHours) {
    const totalMin = Math.round(totalHours * 60);
    const threshMin = Math.min(20, Math.max(10, Math.round((totalMin - 20) * 0.45)));
    const reps = Math.min(3, Math.max(2, Math.floor((totalMin - 20 - threshMin - 5) / 6)));
    const qualityMin = threshMin + 3 + reps * 6;
    const body = [`${fmt(threshMin)} Z4 HR`, `3min Z2 HR`, `${reps}x`,
      `- 3min Z5 HR`, `- 3min Z1 HR`].join("\n");
    return wrapWU(body, totalMin, qualityMin);
  },
};

// ─────────────────────────────────────────────────────────────────────────────
// FARTLEK — Magness / Hudson
// ─────────────────────────────────────────────────────────────────────────────

const structuredFartlek: BankSession = {
  id: "structured-fartlek",
  name: "Strukturerat fartlek",
  rationale: "Magness: 8–12 × (1 min Z5 + 1 min Z2). Låg psykisk belastning, " +
    "hög fysiologisk effekt — bra inledning av ett kvalitetsmikrocykel.",
  sports: RUN_ALL,
  phases: ALL_PHASES,
  distances: ALL_DIST,
  minCTL: 25,
  generate(totalHours) {
    const totalMin = Math.round(totalHours * 60);
    const reps = scaleReps(totalMin - 15, 1, 1, 6, 12);
    const qualityMin = reps * 2;
    const body = [`${reps}x`, `- 1min Z5 HR`, `- 1min Z2 HR`].join("\n");
    return wrapWU(body, totalMin, qualityMin, 7);
  },
};

const longFartlek: BankSession = {
  id: "long-fartlek",
  name: "Långt fartlek",
  rationale: "Hudson: 6–8 × (2 min Z4 + 1 min Z2). Bygger tröskeltid med " +
    "aerob buffert — bra för marathon- och ultraspecifik kondition.",
  sports: RUN_ALL,
  phases: BUILD_PEAK,
  distances: LONG,
  minCTL: 35,
  generate(totalHours) {
    const totalMin = Math.round(totalHours * 60);
    const reps = scaleReps(totalMin - 20, 2, 1, 4, 8);
    const qualityMin = reps * 3;
    const body = [`${reps}x`, `- 2min Z4 HR`, `- 1min Z2 HR`].join("\n");
    return wrapWU(body, totalMin, qualityMin);
  },
};

// ─────────────────────────────────────────────────────────────────────────────
// SPEED / NEUROMUSCULAR — Daniels R-pace / Magness
// ─────────────────────────────────────────────────────────────────────────────

const hillSprints: BankSession = {
  id: "hill-sprints",
  minHours: 0.5,
  name: "Backsprints",
  rationale: "Magness: 8–10 × 10 sek maximal backsprint med full vila. " +
    "Aktiverar snabba muskelfibrer och förbättrar löpekonomi utan hög aerob belastning.",
  sports: RUN_ONLY,
  phases: ALL_PHASES,
  distances: ALL_DIST,
  minCTL: 20,
  generate(totalHours) {
    const totalMin = Math.round(totalHours * 60);
    const reps = 8;
    const setMin = repSetMin(reps, 10, 90); // ~13.3 min
    const cdMin = 5;
    const easyMin = Math.max(10, totalMin - setMin - cdMin);
    return [
      `${fmt(easyMin)} Z2 HR`,
      `${reps}x`,
      `- 10sek Z5 HR`,
      `- 90sek Z1 HR`,
      `${fmt(cdMin)} Z1 HR`,
    ].join("\n");
  },
};

const speedReps: BankSession = {
  id: "speed-reps",
  minHours: 0.5,
  name: "Snabbhetsrep",
  rationale: "Daniels R-tempo: 6–10 × 30 sek vid toppfart med full vila (2–3 min). " +
    "Förbättrar neuromuskulär kraft och löpekonomi (Magness, 2014).",
  sports: RUN_ONLY,
  phases: ALL_PHASES,
  distances: SHORT,
  minCTL: 30,
  generate(totalHours) {
    const totalMin = Math.round(totalHours * 60);
    const reps = 8;
    const setMin = repSetMin(reps, 30, 120); // 8 × 2.5 min = 20 min
    const cdMin = 5;
    const easyMin = Math.max(10, totalMin - setMin - cdMin);
    return [
      `${fmt(easyMin)} Z2 HR`,
      `${reps}x`,
      `- 30sek Z5 HR`,
      `- 2min Z1 HR`,
      `${fmt(cdMin)} Z1 HR`,
    ].join("\n");
  },
};

const strides: BankSession = {
  minHours: 0.4,
  id: "strides",
  name: "Strides (aktiveringslöpning)",
  rationale: "Daniels: 6–8 × 20 sek accelerationer till Z4–5 med 40 sek vila. " +
    "Neuromuskulär aktivering — passar som inledning av taper eller dagen före lopp.",
  sports: RUN_ONLY,
  phases: ["taper", "peak"],
  distances: ALL_DIST,
  minCTL: 20,
  generate(totalHours) {
    const totalMin = Math.round(totalHours * 60);
    const setMin = repSetMin(8, 20, 40); // 8 × 1 min = 8 min
    const cdMin = 5;
    const easyMin = Math.max(10, totalMin - setMin - cdMin);
    return [
      `${fmt(easyMin)} Z2 HR`,
      `8x`,
      `- 20sek Z5 HR`,
      `- 40sek Z1 HR`,
      `${fmt(cdMin)} Z1 HR`,
    ].join("\n");
  },
};

// ─────────────────────────────────────────────────────────────────────────────
// HILL / TERRAIN — trail-specific strength, climbing power & descending
// ─────────────────────────────────────────────────────────────────────────────

const hillRepeats: BankSession = {
  id: "hill-repeats",
  minHours: 0.75,
  name: "Backintervaller",
  rationale: "Magness/Daniels: 4–6 × 2 min i uppförsbacke (Z5) med nedjogg som vila. " +
    "Bygger maximal aerob kraft och specifik klätterstyrka — nyckelpass för terräng/trail.",
  sports: RUN_ONLY,
  phases: BUILD_PEAK,
  distances: ALL_DIST,
  minCTL: 35,
  generate(totalHours) {
    const totalMin = Math.round(totalHours * 60);
    const reps = scaleReps(totalMin - 20, 2, 2, 4, 6);
    const qualityMin = reps * 4; // 2 min up + 2 min jog down
    const body = [`${reps}x`, `- 2min Z5 HR`, `- 2min Z1 HR`].join("\n");
    return wrapWU(body, totalMin, qualityMin);
  },
};

const hillTempo: BankSession = {
  id: "hill-tempo",
  minHours: 0.75,
  name: "Backtempo",
  rationale: "Hudson/Magness: 3–4 × 5 min ihållande uppför i tröskelfart (Z4) med nedjogg. " +
    "Tränar uthållig klätterförmåga och tröskel i stigning — central för trail/berg och ultra.",
  sports: RUN_ONLY,
  phases: BUILD_PEAK,
  distances: ALL_DIST,
  minCTL: 35,
  generate(totalHours) {
    const totalMin = Math.round(totalHours * 60);
    const reps = scaleReps(totalMin - 20, 5, 3, 3, 4);
    const qualityMin = reps * 8; // 5 min up + 3 min down
    const body = [`${reps}x`, `- 5min Z4 HR`, `- 3min Z1 HR`].join("\n");
    return wrapWU(body, totalMin, qualityMin);
  },
};

const downhillRepeats: BankSession = {
  id: "downhill-repeats",
  minHours: 0.5,
  name: "Utförsrepetitioner",
  terrainOnly: true,
  rationale: "Vernillo m.fl. + Nosaka 'repeated bout effect': kontrollerad löpning i " +
    "nedförsbacke tränar excentrisk styrka och tål nedförslöpning — skyddar quadriceps " +
    "inför långa, tekniska nedförslöp i trail/ultra.",
  sports: RUN_ONLY,
  phases: ALL_PHASES,
  distances: ALL_DIST,
  minCTL: 30,
  generate(totalHours) {
    const totalMin = Math.round(totalHours * 60);
    const reps = 6;
    const setMin = repSetMin(reps, 60, 120); // 6 × 3 min = 18 min
    const cdMin = 5;
    const easyMin = Math.max(10, totalMin - setMin - cdMin);
    return [
      `${fmt(easyMin)} Z2 HR`,
      `${reps}x`,
      `- 1min Z3 HR`,   // controlled, quick-cadence downhill
      `- 2min Z1 HR`,
      `${fmt(cdMin)} Z1 HR`,
    ].join("\n");
  },
};

// ─────────────────────────────────────────────────────────────────────────────
// CYCLING-SPECIFIC — Skiba / Coggan
// ─────────────────────────────────────────────────────────────────────────────

const sweetSpot: BankSession = {
  id: "sweet-spot",
  name: "Sweet spot",
  rationale: "Skiba/Coggan: 2–3 × 20 min @ 88–93% FTP (Z3–Z4). " +
    "Hög tröskeltid med lägre trötthet än FTP-intervaller — kärnan i cykelträning.",
  sports: RIDE,
  phases: BUILD_PEAK,
  distances: ALL_DIST,
  minCTL: 30,
  generate(totalHours) {
    const totalMin = Math.round(totalHours * 60);
    const reps = scaleReps(totalMin - 20, 20, 5, 2, 3);
    const qualityMin = reps * 25;
    const body = [`${reps}x`, `- 20min Z4 HR`, `- 5min Z2 HR`].join("\n");
    return wrapWU(body, totalMin, qualityMin);
  },
};

const ftpIntervals: BankSession = {
  id: "ftp-intervals",
  name: "FTP-intervaller",
  rationale: "Coggan: 2 × 20 min @ FTP (Z4) med 5 min vila. " +
    "Klassisk cykeltröskelträning — förbättrar maximal aerob effekt.",
  sports: RIDE,
  phases: BUILD_PEAK,
  distances: ALL_DIST,
  minCTL: 40,
  generate(totalHours) {
    const totalMin = Math.round(totalHours * 60);
    const reps = scaleReps(totalMin - 20, 20, 5, 2, 3);
    const qualityMin = reps * 25;
    const body = [`${reps}x`, `- 20min Z4 HR`, `- 5min Z1 HR`].join("\n");
    return wrapWU(body, totalMin, qualityMin);
  },
};

const cyclingVO2max: BankSession = {
  id: "cycling-vo2max",
  name: "VO2max-intervaller (cykel)",
  rationale: "Skiba: 4–6 × 5 min @ 110–120% FTP (Z5) med 5 min Z1 vila. " +
    "Maximerar syreupptagning på cykel.",
  sports: RIDE,
  phases: BUILD_PEAK,
  distances: ALL_DIST,
  minCTL: 40,
  generate(totalHours) {
    const totalMin = Math.round(totalHours * 60);
    const reps = scaleReps(totalMin - 20, 5, 5, 3, 6);
    const qualityMin = reps * 10;
    const body = [`${reps}x`, `- 5min Z5 HR`, `- 5min Z1 HR`].join("\n");
    return wrapWU(body, totalMin, qualityMin);
  },
};

const microIntervals: BankSession = {
  id: "micro-intervals",
  name: "Mikrointervaller (30/30)",
  rationale: "Skiba: 20–30 × (30 sek Z5 + 30 sek Z1). " +
    "Hög neuromuskulär belastning och VO2max-stimulans med minimal mjölksyreackumulering.",
  sports: RIDE,
  phases: BUILD_PEAK,
  distances: SHORT,
  minCTL: 35,
  generate(totalHours) {
    const totalMin = Math.round(totalHours * 60);
    const reps = Math.min(30, Math.max(15, Math.floor(totalMin - 20)));
    const qualityMin = reps; // 1 min per rep
    const body = [`${reps}x`, `- 30sek Z5 HR`, `- 30sek Z1 HR`].join("\n");
    return wrapWU(body, totalMin, qualityMin);
  },
};

// ─────────────────────────────────────────────────────────────────────────────
// SKI-SPECIFIC
// ─────────────────────────────────────────────────────────────────────────────

const skiThreshold: BankSession = {
  id: "ski-threshold",
  name: "Tröskelintervaller (skidor)",
  rationale: "Daniels T-tempo anpassat för längdskidor: 3–4 × 8 min @ Z4 " +
    "med 2 min Z2 vila. Bygger laktattolerans och ekonomi i diagonalsteg.",
  sports: SKI,
  phases: BUILD_PEAK,
  distances: ALL_DIST,
  minCTL: 30,
  generate(totalHours) {
    const totalMin = Math.round(totalHours * 60);
    const reps = scaleReps(totalMin - 20, 8, 2, 3, 5);
    const qualityMin = reps * 10;
    const body = [`${reps}x`, `- 8min Z4 HR`, `- 2min Z2 HR`].join("\n");
    return wrapWU(body, totalMin, qualityMin);
  },
};

const skiVO2max: BankSession = {
  id: "ski-vo2max",
  name: "VO2max-intervaller (skidor)",
  rationale: "4–6 × 4 min @ Z5 med 3 min Z1 vila. " +
    "Används av elitskidåkare för att maximera syreupptagning inför tävling.",
  sports: SKI,
  phases: BUILD_PEAK,
  distances: ALL_DIST,
  minCTL: 35,
  generate(totalHours) {
    const totalMin = Math.round(totalHours * 60);
    const reps = scaleReps(totalMin - 20, 4, 3, 4, 6);
    const qualityMin = reps * 7;
    const body = [`${reps}x`, `- 4min Z5 HR`, `- 3min Z1 HR`].join("\n");
    return wrapWU(body, totalMin, qualityMin);
  },
};

// Ski-specific AEROBIC/neuromuscular work. Without these the only base-phase
// (general specificity) quality option for skiing was structured fartlek — the
// run-only hill-sprints/strides are excluded for ski, so every base week
// repeated the same fartlek. Magness/Daniels keep speed + economy work present
// year-round; Sandbakk & Holmberg show elite skiers train specific speed and
// uphill strength in the ski modality. These fill that gap and give the base
// phase real rotation.
const skiSpeed: BankSession = {
  id: "ski-speed",
  minHours: 0.4,
  name: "Fart- och teknikpass (skidor)",
  rationale: "Sandbakk & Holmberg: korta accelerationer med teknikfokus (diagonal/stak) " +
    "och full vila. Utvecklar snabbhet och åkekonomi till låg metabol kostnad — " +
    "neuromuskulärt komplement som hålls igång året runt (jfr Daniels strides).",
  sports: SKI,
  phases: ALL_PHASES,
  distances: ALL_DIST,
  minCTL: 20,
  generate(totalHours) {
    const totalMin = Math.round(totalHours * 60);
    const reps = 8;
    const setMin = repSetMin(reps, 20, 90); // ~14.7 min
    const cdMin = 5;
    const easyMin = Math.max(10, totalMin - setMin - cdMin);
    return [
      `${fmt(easyMin)} Z2 HR`,
      `${reps}x`,
      `- 20sek Z5 HR`,
      `- 90sek Z1 HR`,
      `${fmt(cdMin)} Z1 HR`,
    ].join("\n");
  },
};

const skiHillSprints: BankSession = {
  id: "ski-hill-sprints",
  minHours: 0.5,
  name: "Backintervaller (skidor)",
  rationale: "Sandbakk: korta, kraftfulla backrepetitioner i stakning/diagonal bygger " +
    "skidspecifik styrka och åkekonomi med låg aerob belastning — skidåkningens " +
    "motsvarighet till löpningens backsprints (Magness).",
  sports: SKI,
  phases: ALL_PHASES,
  distances: ALL_DIST,
  minCTL: 20,
  generate(totalHours) {
    const totalMin = Math.round(totalHours * 60);
    const reps = 6;
    const setMin = repSetMin(reps, 30, 120); // 15 min
    const cdMin = 5;
    const easyMin = Math.max(10, totalMin - setMin - cdMin);
    return [
      `${fmt(easyMin)} Z2 HR`,
      `${reps}x`,
      `- 30sek Z5 HR`,
      `- 2min Z1 HR`,
      `${fmt(cdMin)} Z1 HR`,
    ].join("\n");
  },
};

const skiHillBounding: BankSession = {
  id: "ski-hill-bounding",
  minHours: 0.5,
  name: "Stavgång i backe (skidor)",
  rationale: "Losnegård/Sandbakk: stavgång och löpning med stavar i uppförsbacke är " +
    "klassisk barmarksträning för längdåkare — bygger stak- och bålstyrka samt " +
    "åkspecifik klätterekonomi till låg aerob kostnad.",
  sports: SKI,
  phases: ALL_PHASES,
  distances: ALL_DIST,
  minCTL: 20,
  generate(totalHours) {
    const totalMin = Math.round(totalHours * 60);
    const reps = 8;
    const setMin = repSetMin(reps, 60, 90); // 8 × 2.5 min = 20 min
    const cdMin = 5;
    const easyMin = Math.max(10, totalMin - setMin - cdMin);
    return [
      `${fmt(easyMin)} Z2 HR`,
      `${reps}x`,
      `- 1min Z4 HR`,
      `- 90sek Z1 HR`,
      `${fmt(cdMin)} Z1 HR`,
    ].join("\n");
  },
};

const skiHillIntervals: BankSession = {
  id: "ski-hill-intervals",
  minHours: 0.75,
  name: "Backintervaller med fart (skidor)",
  rationale: "Sandbakk & Holmberg: längdlopp avgörs i backarna — 4–6 × 3 min uppför i Z5 " +
    "(diagonal/stak) maximerar syreupptagning i åkspecifik klätterposition.",
  sports: SKI,
  phases: BUILD_PEAK,
  distances: ALL_DIST,
  minCTL: 35,
  generate(totalHours) {
    const totalMin = Math.round(totalHours * 60);
    const reps = scaleReps(totalMin - 20, 3, 2, 4, 6);
    const qualityMin = reps * 5; // 3 min up + 2 min down
    const body = [`${reps}x`, `- 3min Z5 HR`, `- 2min Z1 HR`].join("\n");
    return wrapWU(body, totalMin, qualityMin);
  },
};

// Double poling is increasingly decisive in modern XC skiing and loads the
// trunk/arms in a way diagonal-stride work does not (Holmberg; Stöggl &
// Holmberg). Two dedicated sessions: a threshold DP block and an explosive
// DP-specific-strength session.
const skiDoublePoleThreshold: BankSession = {
  id: "ski-double-pole-threshold",
  name: "Stakintervaller (tröskel)",
  rationale: "Holmberg / Stöggl & Holmberg: dubbelstakning är allt mer avgörande i modern " +
    "längdåkning. 4–6 × 5 min @ Z4 i ren stakning bygger åkspecifik tröskelkapacitet och " +
    "bål- och armuthållighet som diagonalpass inte når.",
  sports: SKI,
  phases: BUILD_PEAK,
  distances: ALL_DIST,
  minCTL: 30,
  generate(totalHours) {
    const totalMin = Math.round(totalHours * 60);
    const reps = scaleReps(totalMin - 20, 5, 2, 4, 6);
    const qualityMin = reps * 7; // 5 min on + 2 min off
    const body = [`${reps}x`, `- 5min Z4 HR`, `- 2min Z2 HR`].join("\n");
    return wrapWU(body, totalMin, qualityMin);
  },
};

const skiDoublePoleStrength: BankSession = {
  id: "ski-double-pole-strength",
  minHours: 0.5,
  name: "Stakstyrka (specifik styrka)",
  rationale: "Sandbakk & Holmberg / Stöggl & Holmberg: skidspecifik styrka byggs effektivast " +
    "i åkrörelsen — tunga, explosiva stak på flackt eller svagt motlut med full vila utvecklar " +
    "bål- och armkraft (double-poling power) till låg aerob kostnad. Komplettera gärna med en " +
    "bålcirkel (plankor, rygg, sneda bukar) efteråt.",
  sports: SKI,
  phases: ALL_PHASES,
  distances: ALL_DIST,
  minCTL: 20,
  generate(totalHours) {
    const totalMin = Math.round(totalHours * 60);
    const reps = 10;
    const setMin = repSetMin(reps, 20, 120); // 10 × (20s + 2min) ≈ 23.3 min
    const cdMin = 5;
    const easyMin = Math.max(10, totalMin - setMin - cdMin);
    return [
      `${fmt(easyMin)} Z2 HR`,
      `${reps}x`,
      `- 20sek Z5 HR`,
      `- 2min Z1 HR`,
      `${fmt(cdMin)} Z1 HR`,
    ].join("\n");
  },
};

// ─────────────────────────────────────────────────────────────────────────────
// Full bank
// ─────────────────────────────────────────────────────────────────────────────

export const WORKOUT_BANK: BankSession[] = [
  // Threshold
  cruiseIntervals,
  continuousTempo,
  brokenTempo,
  progressionRun,
  marathonBlocks,
  // VO2max
  classicVO2max,
  shortVO2max,
  longVO2max,
  ladderIntervals,
  // Hybrid
  hybridSession,
  // Fartlek
  structuredFartlek,
  longFartlek,
  // Speed / neuromuscular
  hillSprints,
  speedReps,
  strides,
  // Hill / terrain (trail)
  hillRepeats,
  hillTempo,
  downhillRepeats,
  // Cycling
  sweetSpot,
  ftpIntervals,
  cyclingVO2max,
  microIntervals,
  // Ski
  skiThreshold,
  skiVO2max,
  skiSpeed,
  skiHillSprints,
  skiHillBounding,
  skiHillIntervals,
  skiDoublePoleThreshold,
  skiDoublePoleStrength,
];

// ── Specificity progression ────────────────────────────────────────────────
// Each session is tagged with the energy system / training focus it develops.
// As the race nears, the plan shifts emphasis from general aerobic support →
// threshold → race-specific work (Daniels' phase logic, Bompa's specificity
// principle). "race" focus = work at/near race demands (marathon-pace blocks
// for long events, VO2max for short events — chosen per distance below).
export type Focus = "aerobic" | "threshold" | "vo2max" | "race";

export const SESSION_FOCUS: Record<string, Focus> = {
  // aerobic support / neuromuscular
  "structured-fartlek": "aerobic",
  "long-fartlek": "aerobic",
  "hill-sprints": "aerobic",
  "strides": "aerobic",
  "ski-speed": "aerobic",
  "ski-hill-sprints": "aerobic",
  "ski-hill-bounding": "aerobic",
  "ski-double-pole-strength": "aerobic",
  "downhill-repeats": "aerobic",
  // threshold
  "cruise-intervals": "threshold",
  "continuous-tempo": "threshold",
  "broken-tempo": "threshold",
  "progression-run": "threshold",
  "threshold-vo2max-hybrid": "threshold",
  "sweet-spot": "threshold",
  "ftp-intervals": "threshold",
  "ski-threshold": "threshold",
  "ski-double-pole-threshold": "threshold",
  "hill-tempo": "threshold",
  // vo2max
  "classic-vo2max": "vo2max",
  "short-vo2max": "vo2max",
  "long-vo2max": "vo2max",
  "ladder": "vo2max",
  "speed-reps": "vo2max",
  "cycling-vo2max": "vo2max",
  "micro-intervals": "vo2max",
  "ski-vo2max": "vo2max",
  "hill-repeats": "vo2max",
  "ski-hill-intervals": "vo2max",
  // race-specific
  "marathon-blocks": "race",
};

export type Specificity = "general" | "threshold" | "race";

/** Resolve a session's focus: custom sessions carry it inline, built-ins use the map. */
export function focusOf(s: BankSession): Focus {
  return s.focus ?? SESSION_FOCUS[s.id] ?? "aerobic";
}

/** Flat metadata for every built-in session — for the planner's "interval bank" UI. */
export interface BankSessionInfo {
  id: string;
  name: string;
  rationale: string;
  focus: Focus;
  sports: SportType[];
  distances: RaceDistance[];
  minCTL: number;
  minHours?: number;
  terrainOnly?: boolean;
  example: string;   // a representative generated workout
}

export function listBankSessions(): BankSessionInfo[] {
  return WORKOUT_BANK.map(s => ({
    id: s.id,
    name: s.name,
    rationale: s.rationale,
    focus: focusOf(s),
    sports: s.sports,
    distances: s.distances,
    minCTL: s.minCTL,
    minHours: s.minHours,
    terrainOnly: s.terrainOnly,
    example: s.generate(Math.max(s.minHours ?? 0, 1)),
  }));
}

// Ordered focus preference for a given specificity + race distance. The picker
// walks this list and uses the first tier that has eligible candidates.
function focusPriority(spec: Specificity, dist: RaceDistance): Focus[] {
  if (spec === "general")   return ["aerobic", "threshold"];
  if (spec === "threshold") return ["threshold", "vo2max", "aerobic"];
  // race-specific — emphasise the system that matters for the distance
  if (dist === "5k" || dist === "10k") return ["vo2max", "threshold"];
  if (dist === "half")                  return ["threshold", "vo2max"];
  if (dist === "marathon" || dist === "ultra") return ["race", "threshold"];
  return ["threshold", "vo2max"];
}

/**
 * Pick an interval session from the bank, progressing toward race specificity.
 *
 * Candidates are first filtered by sport / distance / CTL / minimum duration,
 * then the focus-priority list for the week's specificity selects the tier.
 * Within a tier we rotate deterministically by date so the same plan is stable
 * but consecutive weeks vary.
 */
// Hill / terrain sessions, favoured (rotated among) for trail/terrain races.
const HILL_IDS = new Set([
  "hill-sprints", "hill-repeats", "hill-tempo", "downhill-repeats",
  "ski-hill-sprints", "ski-hill-bounding", "ski-hill-intervals",
]);

export function pickIntervalSession(
  sport: SportType,
  specificity: Specificity,
  dist: RaceDistance,
  ctl: number,
  totalHours: number,
  dateStr: string,  // "YYYY-MM-DD" — used as rotation seed
  seedOffset = 0,   // vary rotation so two sessions on the same day differ
  preferHills = false, // trail races → bias the chosen tier toward hill work
  extraSessions: BankSession[] = [],     // user-added custom sessions, merged into the bank
  favoriteIds: Set<string> = new Set(),  // starred sessions — preferred within their tier
): { name: string; description: string; rationale: string } {
  const bank = extraSessions.length > 0 ? [...WORKOUT_BANK, ...extraSessions] : WORKOUT_BANK;
  const eligible = bank.filter(s =>
    s.sports.includes(sport) &&
    s.distances.includes(dist) &&
    ctl >= s.minCTL &&
    totalHours >= (s.minHours ?? 0) &&
    (!s.terrainOnly || preferHills)   // terrain-only sessions stay out of road rotation
  );

  const seed = (parseInt(dateStr.replace(/-/g, ""), 10) || 0) + seedOffset;

  // Favorites are a bias, not an override: when a tier the phase logic already
  // chose contains starred sessions, rotate only among those. Eligibility and
  // focus priority still gate everything, so a favorite never breaks specificity.
  const preferFavs = (pool: BankSession[]): BankSession[] => {
    const favs = pool.filter(s => favoriteIds.has(s.id));
    return favs.length > 0 ? favs : pool;
  };

  for (const focus of focusPriority(specificity, dist)) {
    const tier = eligible.filter(s => focusOf(s) === focus);
    if (tier.length > 0) {
      // For trail, pick a hill session from this tier when one exists (the
      // aerobic tier holds the hill work); otherwise rotate normally.
      const pool = preferHills ? tier.filter(s => HILL_IDS.has(s.id)) : [];
      const chosen = preferFavs(pool.length > 0 ? pool : tier);
      const picked = chosen[seed % chosen.length];
      return { name: picked.name, description: picked.generate(totalHours), rationale: picked.rationale };
    }
  }

  // Fallback: any eligible session, else any for the sport, else a generic set.
  const pool = eligible.length > 0 ? eligible : bank.filter(s => s.sports.includes(sport));
  if (pool.length === 0) {
    return {
      name: "Intervaller",
      description: `10min Z2 HR\n4x\n- 4min Z5 HR\n- 4min Z1 HR\n10min Z1 HR`,
      rationale: "",
    };
  }
  const favPool = preferFavs(pool);
  const picked = favPool[seed % favPool.length];
  return { name: picked.name, description: picked.generate(totalHours), rationale: picked.rationale };
}
